// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Presentation for the organization's serial patterns. Matching lives on the
 * backend; these examples illustrate, they do not decide.
 */
import type { SerialPattern, SerialPatternKind } from '@/account/api/types';
import { SERIAL_PATTERN_KINDS } from '@/account/api/types';
import { pluralize } from '@/utilities/pluralize';

/** Lowest and highest character count a sized pattern may require. */
export const SERIAL_PATTERN_LENGTH_MIN = 1;
export const SERIAL_PATTERN_LENGTH_MAX = 32;

/** Longest literal prefix a prefixed pattern may carry. */
export const SERIAL_PATTERN_PREFIX_MAX_LENGTH = 16;

/** How many patterns one organization may configure. */
export const MAX_SERIAL_PATTERNS = 10;

/** Characters a prefix may contain. Mirrors the backend's rule. */
export const SERIAL_PATTERN_PREFIX_PATTERN = /^[A-Za-z0-9._-]+$/;

interface SerialPatternKindDisplay {
  label: string;
  /** Where the kind sits in the picker's list. */
  pickerOrder: number;
  /** One line saying what the kind accepts, for the picker's tooltip. */
  description: string;
  /** Whether the kind is sized by a length the user chooses. */
  takesLength: boolean;
  /** Characters an example cycles through, for the sized kinds. */
  exampleCharacters: string;
  /** The length an example uses before the row has one. */
  sampleLength: number;
  /** Every fixed form the kind accepts, for the kinds that are not sized. */
  fixedExamples: string[];
}

export const SERIAL_PATTERN_KIND_DISPLAY: Record<
  SerialPatternKind,
  SerialPatternKindDisplay
> = {
  alpha: {
    label: 'Letters only',
    pickerOrder: 1,
    description: 'Letters only, of the length you choose',
    takesLength: true,
    exampleCharacters: 'KJMZQWXRBNTYVCHLPSDG',
    sampleLength: 8,
    fixedExamples: [],
  },
  alphanumeric: {
    label: 'Alphanumeric',
    pickerOrder: 0,
    description: 'Letters and numbers, of the length you choose',
    takesLength: true,
    exampleCharacters: '4KJ92MZ0QW18XR7BN3TY6VC5HL1PS0DG',
    sampleLength: 12,
    fixedExamples: [],
  },
  numeric: {
    label: 'Numbers only',
    pickerOrder: 2,
    description: 'Numbers only, of the length you choose',
    takesLength: true,
    exampleCharacters: '0048192037',
    sampleLength: 8,
    fixedExamples: [],
  },
  hex: {
    label: 'Hexadecimal',
    pickerOrder: 3,
    description: 'Numbers and the letters A to F, of the length you choose',
    takesLength: true,
    exampleCharacters: '3C22FB1A9004E7D5',
    sampleLength: 8,
    fixedExamples: [],
  },
  mac_address: {
    label: 'MAC address',
    pickerOrder: 4,
    description: 'Six hex pairs, separated or run together',
    takesLength: false,
    exampleCharacters: '',
    sampleLength: 0,
    fixedExamples: [
      '3C:22:FB:1A:90:04',
      '3C-22-FB-1A-90-04',
      '3c22.fb1a.9004',
      '3c22fb1a9004',
    ],
  },
  uuid: {
    label: 'UUID',
    pickerOrder: 5,
    description: 'A UUID, with or without dashes',
    takesLength: false,
    exampleCharacters: '',
    sampleLength: 0,
    fixedExamples: [
      '550e8400-e29b-41d4-a716-446655440000',
      '550e8400e29b41d4a716446655440000',
    ],
  },
};

/**
 * Kinds in the order the picker lists them, derived from the display record so
 * a new kind cannot be added without placing it.
 */
export const SERIAL_PATTERN_KIND_ORDER: SerialPatternKind[] =
  SERIAL_PATTERN_KINDS.slice().sort(
    (left, right) =>
      SERIAL_PATTERN_KIND_DISPLAY[left].pickerOrder -
      SERIAL_PATTERN_KIND_DISPLAY[right].pickerOrder
  );

/** Every length a sized pattern may require. */
export const SERIAL_PATTERN_LENGTH_OPTIONS = Array.from(
  { length: SERIAL_PATTERN_LENGTH_MAX - SERIAL_PATTERN_LENGTH_MIN + 1 },
  (_unused, index) => SERIAL_PATTERN_LENGTH_MIN + index
);

function cycleCharacters(characters: string, length: number): string {
  let out = '';
  for (let index = 0; index < length; index += 1) {
    out += characters[index % characters.length];
  }

  return out;
}

export interface SerialPatternExamples {
  /** Every form the pattern accepts. Never empty. */
  values: string[];
  /** False when a sample length stood in for one the row lacks. */
  isExact: boolean;
}

/** Serials the pattern accepts, falling back to a sample for a half-filled row. */
export function serialPatternExamples(
  pattern: SerialPattern
): SerialPatternExamples {
  const display = SERIAL_PATTERN_KIND_DISPLAY[pattern.kind];
  const prefix = pattern.prefix ?? '';

  if (!display.takesLength) {
    return {
      values: display.fixedExamples.map((example) => `${prefix}${example}`),
      isExact: true,
    };
  }

  const hasLength =
    pattern.length !== null &&
    pattern.length >= SERIAL_PATTERN_LENGTH_MIN &&
    pattern.length <= SERIAL_PATTERN_LENGTH_MAX;
  const length = hasLength ? Number(pattern.length) : display.sampleLength;

  return {
    values: [`${prefix}${cycleCharacters(display.exampleCharacters, length)}`],
    isExact: hasLength,
  };
}

/** Serials a kind accepts, before any length or prefix has been chosen. */
export function serialPatternKindExamples(kind: SerialPatternKind): string[] {
  return serialPatternExamples({ kind, length: null, prefix: null }).values;
}

/** How the card summarizes one configured pattern. */
export function serialPatternSummary(pattern: SerialPattern): string {
  const display = SERIAL_PATTERN_KIND_DISPLAY[pattern.kind];
  const parts = [display.label];
  if (pattern.prefix !== null && pattern.prefix !== '') {
    parts.push(`"${pattern.prefix}" prefix`);
  }
  if (display.takesLength && pattern.length !== null) {
    parts.push(pluralize(pattern.length, { singular: 'character' }));
  }

  return parts.join(', ');
}
